import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EventAssociationType } from '@/types/events';
import { toast } from 'sonner';

export interface EventAssociationTypeInsert {
  name: string;
  label: string;
}

export function useEventAssociationTypes() {
  const queryClient = useQueryClient();

  const { data: types = [], isLoading } = useQuery({
    queryKey: ['event-association-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_event_association_types')
        .select('id, name, label, created_at')
        .order('label');
      if (error) throw error;
      return (data ?? []) as EventAssociationType[];
    },
  });

  const createType = useMutation({
    mutationFn: async (payload: EventAssociationTypeInsert) => {
      const { data, error } = await supabase
        .from('os_event_association_types')
        .insert(payload as never)
        .select()
        .single();
      if (error) throw error;
      return data as EventAssociationType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-association-types'] });
      toast.success('Association type created');
    },
    onError: (e: unknown) => {
      const msg = typeof e === 'object' && e !== null && 'message' in e ? String((e as { message: unknown }).message) : e instanceof Error ? e.message : 'Failed to create association type';
      toast.error(msg);
    },
  });

  const updateType = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<EventAssociationType> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_event_association_types')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as EventAssociationType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-association-types'] });
      toast.success('Association type updated');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Failed to update association type'),
  });

  const deleteType = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('os_event_association_types').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-association-types'] });
      toast.success('Association type deleted');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Failed to delete association type (may be in use)'),
  });

  return { types, isLoading, createType, updateType, deleteType };
}
